// Реалистичные данные витрины (детерминированные: одинаковая картинка при каждом открытии).
import type { Point } from '@/components/charts/scale';
import type { EntityKind } from '@/components/ui/entityIcons';
import type { TimelineKind } from '@/components/ui';

function mulberry32(seed: number) {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** CPU за 24 часа, шаг 5 минут: суточная волна + всплеск с хвостом около 17:50 относительно начала. */
export function cpuSeries(now = Date.now()): Point[] {
  const rnd = mulberry32(42);
  const step = 5 * 60 * 1000;
  const start = Math.floor((now - 24 * 3600 * 1000) / step) * step;
  const pts: Point[] = [];
  for (let i = 0; i <= 288; i++) {
    const hour = (i * 5) / 60;
    const wave = 28 + 6 * Math.sin((hour / 24) * Math.PI * 2 - 1.2);
    const d = hour - 17.8;
    const spike = 46 * Math.exp(-(d * d) / (2 * 0.35 * 0.35));
    const tail = d > 0 ? 18 * Math.exp(-d / 1.2) : 0;
    const v = wave + spike + tail + (rnd() - 0.5) * 4;
    pts.push({ x: start + i * step, y: Math.round(Math.max(3, Math.min(98, v)) * 10) / 10 });
  }
  return pts;
}

export const trends = {
  cpu: [24, 26, 25, 29, 31, 30, 34, 62, 78, 55, 41, 34],
  la: [0.3, 0.32, 0.31, 0.35, 0.4, 0.38, 0.42, 1.9, 8.4, 3.1, 0.9, 0.42],
  mem: [58, 58, 59, 59, 60, 60, 61, 61, 62, 61, 61, 61],
  net: [0.8, 0.9, 0.7, 1.1, 1.0, 1.2, 1.4, 2.6, 2.1, 1.3, 1.1, 1.2],
};

export const problems = [
  {
    id: 'p1',
    sev: 4,
    name: 'Load average выше 8 за 5 мин',
    target: { kind: 'host' as EntityKind, name: 'host-338' },
    since: '34 мин',
    acked: false,
    tags: ['cpu', 'pve'],
  },
  {
    id: 'p2',
    sev: 4,
    name: 'vhost tx_dropped растёт на vnet14',
    target: { kind: 'vm' as EntityKind, name: 'VM 100101' },
    since: '18 мин',
    acked: true,
    ackBy: 'Анна',
    tags: ['сеть'],
  },
  {
    id: 'p3',
    sev: 3,
    name: 'Свободно меньше 10 % на /vds',
    target: { kind: 'host' as EntityKind, name: 'host-338' },
    since: '2 ч',
    acked: false,
    tags: ['диск'],
  },
  {
    id: 'p4',
    sev: 2,
    name: 'Агент метрик не отвечал 3 мин',
    target: { kind: 'vm' as EntityKind, name: 'VM 100103' },
    since: '5 ч',
    acked: false,
    tags: ['агент'],
  },
  {
    id: 'p5',
    sev: 1,
    name: 'Доступно обновление PVE 8.2.4',
    target: { kind: 'host' as EntityKind, name: 'host-338' },
    since: '2 дн',
    acked: true,
    ackBy: 'Михаил',
    tags: ['обновления'],
  },
];

export const severityLabels = ['Not classified', 'Information', 'Warning', 'Average', 'High', 'Disaster'];
export const severityPill = [
  'bg-sev-0 text-on-sev-0',
  'bg-sev-1 text-on-sev-1',
  'bg-sev-2 text-on-sev-2',
  'bg-sev-3 text-on-sev-3',
  'bg-sev-4 text-on-sev-4',
  'bg-sev-5 text-on-sev-5',
];

export const timeline: {
  id: string;
  kind: TimelineKind;
  time: string;
  title: string;
  description?: string;
  actor?: string;
}[] = [
  {
    id: 't1',
    kind: 'ack',
    time: '12:19',
    title: 'Подтверждено',
    actor: 'Анна',
    description: '«смотрю, похоже на бэкап у клиента»',
  },
  {
    id: 't2',
    kind: 'ai',
    time: '12:14',
    title: 'AI: похоже на всплеск ввода-вывода у VM 100101',
    description: 'гипотеза по vhost tx_dropped и iowait, уверенность 0.72',
  },
  {
    id: 't3',
    kind: 'alert',
    time: '12:13',
    title: 'Триггер High: LA > 8',
    description: 'Метрики · host-338',
  },
  {
    id: 't4',
    kind: 'change',
    time: '11:40',
    title: 'Изменение в CMDB: добавлен аплинк 10G',
    actor: 'Михаил',
  },
  {
    id: 't5',
    kind: 'resolve',
    time: 'вчера 03:12',
    title: 'Восстановлено: свободная память',
    description: 'длительность 22 мин',
  },
];

export const tasks = [
  {
    id: 'k1',
    title: 'Собрать bond на host-006 до пятницы',
    due: 'пт',
    assignee: 'Анна Смирнова',
    done: false,
    linked: 'TASK-412',
  },
  {
    id: 'k2',
    title: 'Проверить fsck.repair=yes в шаблонах',
    due: 'сегодня',
    assignee: 'Анна Смирнова',
    done: true,
  },
  { id: 'k3', title: 'Согласовать окно обслуживания host-338', due: 'вт', assignee: 'Михаил', done: false },
];

export const mentions = [
  {
    id: 'm1',
    channel: '#alerts-infra',
    who: 'Михаил',
    text: '@anna глянь host-338, LA ползёт уже минут двадцать',
    time: '12:16',
    unread: true,
  },
  {
    id: 'm2',
    channel: '#ops',
    who: 'Елена',
    text: 'На host-002 сбой миграции VolDelete. Посмотри позже?',
    time: '11:02',
    unread: false,
  },
];

export const related: { kind: EntityKind; name: string; meta?: string }[] = [
  { kind: 'location', name: 'Площадка А', meta: 'DC-1' },
  { kind: 'channel', name: '#alerts-infra' },
  { kind: 'ticket', name: 'TASK-412' },
  { kind: 'host', name: 'host-monitor', meta: 'центр мониторинга' },
  { kind: 'user', name: 'Михаил', meta: 'владелец' },
];

export const workspaceTabs = [
  { id: 'host-338', kind: 'host' as EntityKind, title: 'host-338', pinned: true, dotClassName: 'bg-sev-4' },
  { id: 'inc', kind: 'incident' as EntityKind, title: 'INC-2189 · LA на host-338', dotClassName: 'bg-sev-4' },
  { id: 'mail', kind: 'mail' as EntityKind, title: 'Михаил: bond на host-006' },
  { id: 'task', kind: 'task' as EntityKind, title: 'Задача: fsck.repair' },
  { id: 'vm', kind: 'vm' as EntityKind, title: 'VM 100101' },
];
